import { Dropdown } from "antd";
import {
  DotsThreeVertical,
  Export,
  FolderSimplePlus,
  LinkSimple,
  PencilSimple,
  ShareNetwork,
  Trash,
} from "@phosphor-icons/react";

const DANGER_FG = "#c63939";

// antd's Menu `items` onClick receives a MenuInfo, not a plain click event —
// the menu itself renders into a portal, so without this, the click still
// bubbles through the React tree (not the DOM tree) to the row's own
// onClick and fires "open" alongside whatever action was actually picked.
const withStopPropagation = (handler) => (info) => {
  info?.domEvent?.stopPropagation?.();
  handler();
};

// Row-level action menu for a folder inside My Files. Deliberately smaller
// than the matter tree's FolderContextMenu — no clipboard paste, no
// matter-scoped operations, and the root "My Files" folder itself can be
// neither renamed nor deleted (enforced again server-side).
export function FolderActionsMenu({
  folder,
  isRoot,
  onNewSubfolder,
  onRename,
  onDelete,
  onExternalShare,
}) {
  const items = [
    {
      key: "new-subfolder",
      label: "New subfolder",
      icon: <FolderSimplePlus size={16} />,
      onClick: withStopPropagation(() => onNewSubfolder(folder)),
    },
    ...(onExternalShare
      ? [
          {
            key: "external-share",
            label: "Share folder externally",
            icon: <ShareNetwork size={16} />,
            onClick: withStopPropagation(() => onExternalShare(folder)),
          },
        ]
      : []),
    ...(isRoot
      ? []
      : [
          {
            key: "rename",
            label: "Rename",
            icon: <PencilSimple size={16} />,
            onClick: withStopPropagation(() => onRename(folder)),
          },
          { key: "divider-1", type: "divider" },
          {
            key: "delete",
            label: <span style={{ color: DANGER_FG }}>Delete</span>,
            danger: true,
            icon: <Trash size={16} color={DANGER_FG} />,
            onClick: withStopPropagation(() => onDelete(folder)),
          },
        ]),
  ];

  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <button
        type="button"
        aria-label={`Actions for ${folder.name}`}
        onClick={(event) => event.stopPropagation()}
        style={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          display: "inline-flex",
          padding: 4,
        }}
      >
        <DotsThreeVertical size={18} color="#66758a" />
      </button>
    </Dropdown>
  );
}

// Row-level action menu for a document inside My Files. "Send for Review"
// is intentionally absent (see documents/views/document_share.py block);
// "External Share" is kept exactly as-is per the plan, wired straight into
// the existing ExternalShare/ShareFileModal component.
export function DocumentActionsMenu({
  document,
  onOpen,
  onRename,
  onMove,
  onCopyLink,
  onExternalShare,
  onDelete,
}) {
  const items = [
    ...(onOpen
      ? [{ key: "open", label: "Open", onClick: withStopPropagation(() => onOpen(document)) }]
      : []),
    {
      key: "rename",
      label: "Rename",
      icon: <PencilSimple size={16} />,
      onClick: withStopPropagation(() => onRename(document)),
    },
    {
      key: "move",
      label: "Move to folder",
      icon: <Export size={16} />,
      onClick: withStopPropagation(() => onMove(document)),
    },
    { key: "divider-1", type: "divider" },
    {
      key: "copy-link",
      label: "Copy internal link",
      icon: <LinkSimple size={16} />,
      onClick: withStopPropagation(() => onCopyLink(document)),
    },
    {
      key: "external-share",
      label: "External Share",
      icon: <ShareNetwork size={16} />,
      onClick: withStopPropagation(() => onExternalShare(document)),
    },
    { key: "divider-2", type: "divider" },
    {
      key: "delete",
      label: <span style={{ color: DANGER_FG }}>Delete</span>,
      danger: true,
      icon: <Trash size={16} color={DANGER_FG} />,
      onClick: withStopPropagation(() => onDelete(document)),
    },
  ];

  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <button
        type="button"
        aria-label={`Actions for ${document.name}`}
        onClick={(event) => event.stopPropagation()}
        style={{
          border: "none",
          background: "transparent",
          cursor: "pointer",
          display: "inline-flex",
          padding: 4,
        }}
      >
        <DotsThreeVertical size={18} color="#66758a" />
      </button>
    </Dropdown>
  );
}
